import { FormGroup, InputGroup, NumericInputProps, Tag, Position, HTMLInputProps } from '@blueprintjs/core'
import * as React from 'react'
import Select from 'react-select'
import NullSafeNumericInput from '../../../../../components/NullSafeNumericInput'
import { renderError, stylizeTitle } from '../Helpers'
import { GutterTooltipError } from '../GutterTooltipError'
import { ProductFormGroup } from '../ProductFormGroup'

export type GoodwillFindsOnChangeHandler = ({ name, value }: { name: string; value: any }) => void

interface InputProps {
  error?: any
  onChange?: GoodwillFindsOnChangeHandler

  errorTooltipOffset?: number // Eslint doesn't like how only some usages of InputProps consume errorTooltipOffset
  // eslint-disable-next-line react/no-unused-prop-types
  errorTooltipPosition?: string
}

interface ListingInputProps extends InputProps {
  value?: any
  errors?: any
  showRequiredLabel?: boolean
}

interface PriceInputProps extends InputProps {
  useCAD?: boolean
  showRequiredLabel?: boolean
  onChange: (e: any) => void
}

export const PriceInput = React.memo((props: PriceInputProps & NumericInputProps & Omit<HTMLInputProps, 'size'>) => (
  <ProductFormGroup
    title="Price"
    error={props.error}
    showRequiredLabel={props.showRequiredLabel}
    helperText={renderError(props.error)}
  >
    <GutterTooltipError isOpen={props.error} offsetPx={props.errorTooltipOffset} position={Position.LEFT}>
      <NullSafeNumericInput
        min={0.01}
        placeholder={props.placeholder}
        clampValueOnBlur
        leftIcon="dollar"
        minorStepSize={0.01}
        selectAllOnFocus
        name="price"
        onValueChange={(_, value) => props.onChange({ name: 'price', value })}
        value={props.value || null}
        intent={props.error ? 'danger' : 'none'}
        rightElement={props.useCAD ? <Tag minimal>CAD</Tag> : undefined}
        disabled={props.disabled}
        title={props.title}
        fill
      />
    </GutterTooltipError>
  </ProductFormGroup>
))

export const GwfListingDuration: React.FC<
  ListingInputProps & { includeBlank?: boolean; clearable?: boolean; placeholder?: string; disabled?: boolean }
> = React.memo(({ error, showRequiredLabel, ...props }) => {
  const options = [
    { name: '1 Day', value: 1 },
    { name: '3 Days', value: 3 },
    { name: '5 Days', value: 5 },
    { name: '7 Days', value: 7 },
    { name: '10 Days', value: 10 },
    { name: '30 Days', value: 30 },
    { name: '60 Days', value: 60 },
    { name: '90 Days', value: 90 },
    { name: '120 Days', value: 120 },
    { name: '150 Days', value: 150 },
    { name: '180 Days', value: 180 },
    { name: '210 Days', value: 210 }
  ]
  if (props.includeBlank) {
    options.unshift({ name: 'None', value: 0 })
  }
  return (
    <ProductFormGroup title="Listing Duration" error={error} showRequiredLabel={showRequiredLabel}>
      <GutterTooltipError isOpen={error} offsetPx={props.errorTooltipOffset} position={Position.LEFT}>
        <Select
          value={props.value}
          onChange={value => props.onChange({ name: 'duration', value })}
          labelKey="name"
          valueKey="value"
          clearable={props.clearable || false}
          placeholder={props.placeholder || 'Select...'}
          simpleValue
          disabled={props.disabled}
          options={options}
        />
      </GutterTooltipError>
    </ProductFormGroup>
  )
})

export const QuantityInput = React.memo((props: InputProps & NumericInputProps) => (
  <FormGroup label={stylizeTitle('Quantity', props.error)} helperText={renderError(props.error)}>
    <GutterTooltipError isOpen={props.error} offsetPx={props.errorTooltipOffset} position={props.errorTooltipPosition}>
      <NullSafeNumericInput
        min={0}
        placeholder={props.placeholder}
        clampValueOnBlur
        minorStepSize={1}
        selectAllOnFocus
        name="quantity"
        onValueChange={(_, value) => props.onChange({ name: 'quantity', value })}
        value={props.value || null}
        disabled={props.disabled}
        intent={props.error ? 'danger' : 'none'}
        fill
      />
    </GutterTooltipError>
  </FormGroup>
))

interface ShortDescriptionProps {
  onChange: Function
  value?: any
}

export const ShortDescription: React.FC<ShortDescriptionProps & InputProps> = React.memo(props => {
  return (
    <ProductFormGroup
      title="Short Description"
      error={props.error}
      showRequiredLabel
      helperText={renderError(props.error)}
    >
      <GutterTooltipError
        isOpen={props.error}
        offsetPx={props.errorTooltipOffset}
        position={props.errorTooltipPosition}
      >
        <InputGroup
          value={props.value || ''}
          onChange={e => props.onChange({ name: 'shortDescription', value: e?.currentTarget?.value })}
        />
      </GutterTooltipError>
    </ProductFormGroup>
  )
})

interface ConditionInputProps {
  onChange: Function
  value?: any
}

const GoodwillfindsConditions = [
  'New',
  'New Open Box',
  'Pre Owned, For Repair or For Parts',
  'Pre Owned, Fully Tested',
  'Pre Owned, Power onTested',
  'Used, Excellent',
  'Used, Good',
  'Used, Fair'
]

export const ConditionInput: React.FC<ConditionInputProps & InputProps> = React.memo(props => {
  return (
    <ProductFormGroup title="Condition" error={props.error} showRequiredLabel helperText={renderError(props.error)}>
      <GutterTooltipError
        isOpen={props.error}
        offsetPx={props.errorTooltipOffset}
        position={props.errorTooltipPosition}
      >
        <Select
          options={GoodwillfindsConditions.map(value => {
            return { label: value, value }
          })}
          value={props.value}
          onChange={e => {
            const newValue = e?.value ? e.value : null // handles clearable action
            return props.onChange({ name: 'condition', value: newValue })
          }}
          clearable
        />
      </GutterTooltipError>
    </ProductFormGroup>
  )
})
